import { useEffect, useState } from "react";
import AdminShell from "@/components/AdminShell";
import api from "@/lib/api";
import { useTaxonomy } from "@/context/TaxonomyContext";
import MultiSelect from "@/components/MultiSelect";
import { MatchScore, MatchBadge } from "@/components/MatchScore";
import { toast } from "sonner";
import { Plus, ClipboardList, Sparkles, CheckCircle2, X } from "lucide-react";

const inputCls = "w-full text-sm border border-slate-300 rounded-md px-3 py-2 bg-white focus:border-[#D96B27] outline-none";

export default function AdminRequirements() {
  const tax = useTaxonomy();
  const [reqs, setReqs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [active, setActive] = useState(null);
  const [matches, setMatches] = useState(null);
  const [explain, setExplain] = useState({});

  const load = () => api.get("/admin/requirements").then((r) => setReqs(r.data));
  useEffect(() => { load(); }, []);

  const openMatches = async (req) => {
    setActive(req); setMatches(null);
    const r = await api.get(`/admin/requirements/${req.requirement_id}/candidates`);
    setMatches(r.data);
  };

  const aiExplain = async (candId) => {
    setExplain((e) => ({ ...e, [candId]: "…" }));
    const r = await api.post(`/admin/requirements/${active.requirement_id}/ai-explain/${candId}`);
    setExplain((e) => ({ ...e, [candId]: r.data.explanation }));
  };

  return (
    <AdminShell>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Recruitment Requirements</h1>
          <p className="text-slate-500 text-sm">Create a hiring requirement and auto-match candidates.</p>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="new-requirement-btn" className="flex items-center gap-2 copper-gradient text-white font-semibold px-5 py-2.5 rounded-lg"><Plus className="w-4 h-4" />New Requirement</button>
      </div>

      <div className="grid lg:grid-cols-[360px_1fr] gap-5">
        <div className="space-y-3">
          {reqs.length === 0 ? <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500"><ClipboardList className="w-8 h-8 mx-auto mb-2 text-slate-300" />No requirements yet.</div> :
            reqs.map((r) => (
              <button key={r.requirement_id} onClick={() => openMatches(r)} data-testid={`req-${r.requirement_id}`} className={`w-full text-left bg-white rounded-xl border p-4 transition-colors ${active?.requirement_id === r.requirement_id ? "border-[#D96B27]" : "border-slate-200 hover:border-slate-300"}`}>
                <div className="font-display font-semibold text-slate-900">{r.position || "Untitled requirement"}</div>
                <div className="text-sm text-slate-500">{[r.client, r.location].filter(Boolean).join(" · ") || "—"}</div>
                <div className="flex flex-wrap gap-1 mt-2">{(r.products || []).slice(0, 3).map((p) => <span key={p} className="text-xs bg-[#FFF3EC] text-[#C05615] px-1.5 py-0.5 rounded">{p}</span>)}</div>
              </button>
            ))}
        </div>

        <div>
          {!active ? <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400">Select a requirement to see matching candidates.</div> :
            !matches ? <div className="h-64 bg-white rounded-xl animate-pulse" /> : (
              <div>
                <div className="bg-slate-900 text-white rounded-xl p-5 mb-4">
                  <h2 className="font-display text-xl font-bold">{active.position}</h2>
                  <p className="text-slate-300 text-sm">{matches.total} matching candidates found</p>
                  <div className="flex gap-3 mt-3">
                    {Object.entries(matches.bands).map(([b, n]) => <span key={b} className="text-xs bg-white/10 px-2.5 py-1 rounded">{b}: {n}</span>)}
                  </div>
                </div>
                <div className="space-y-3">
                  {matches.candidates.map((c) => (
                    <div key={c.candidate_id} className="bg-white rounded-xl border border-slate-200 p-4">
                      <div className="flex gap-4">
                        <div className="text-center"><MatchScore score={c.match_score} /><div className="mt-1"><MatchBadge band={c.match_band} /></div></div>
                        <div className="flex-1">
                          <h3 className="font-display font-semibold text-slate-900">{c.full_name}</h3>
                          <p className="text-sm text-slate-500">{c.current_designation} · {c.total_experience} yrs · {c.current_location}</p>
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5">
                            {(c.match_reasons || []).slice(0, 5).map((r, i) => <span key={i} className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />{r}</span>)}
                          </div>
                          <button onClick={() => aiExplain(c.candidate_id)} data-testid={`ai-explain-${c.candidate_id}`} className="mt-2 text-xs flex items-center gap-1 text-[#D96B27] font-medium"><Sparkles className="w-3.5 h-3.5" />AI Match Insight</button>
                          {explain[c.candidate_id] && <p className="mt-1 text-xs text-slate-600 bg-slate-50 rounded p-2">{explain[c.candidate_id]}</p>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
        </div>
      </div>

      {showForm && <RequirementForm tax={tax} onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
    </AdminShell>
  );
}

function RequirementForm({ tax, onClose, onCreated }) {
  const [f, setF] = useState({ client: "", position: "", location: "", min_experience: "", max_experience: "", min_cable_experience: "", products: [], processes: [], skills: [], education: [], education_specialization: "", departments: [], designations: [], locations: [], salary_range: "", required_count: 1 });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async () => {
    if (!f.position) { toast.error("Position is required"); return; }
    const payload = { ...f, min_experience: parseFloat(f.min_experience) || null, max_experience: parseFloat(f.max_experience) || null, min_cable_experience: parseFloat(f.min_cable_experience) || null, locations: f.location ? [f.location, ...f.locations] : f.locations };
    await api.post("/admin/requirements", payload);
    toast.success("Requirement created"); onCreated();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-thin p-6">
        <div className="flex items-center justify-between mb-4"><h2 className="font-display font-bold text-lg">New Requirement</h2><button onClick={onClose}><X className="w-5 h-5 text-slate-500" /></button></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="text-xs font-semibold text-slate-500 uppercase">Client</label><input className={inputCls} data-testid="req-client" value={f.client} onChange={(e) => set("client", e.target.value)} /></div>
          <div><label className="text-xs font-semibold text-slate-500 uppercase">Position</label><input className={inputCls} data-testid="req-position" value={f.position} onChange={(e) => set("position", e.target.value)} /></div>
          <div><label className="text-xs font-semibold text-slate-500 uppercase">Location</label><input list="rlocs" className={inputCls} value={f.location} onChange={(e) => set("location", e.target.value)} /><datalist id="rlocs">{(tax.locations || []).map((l) => <option key={l} value={l} />)}</datalist></div>
          <div><label className="text-xs font-semibold text-slate-500 uppercase">Salary Range</label><input className={inputCls} placeholder="₹12-18 LPA" value={f.salary_range} onChange={(e) => set("salary_range", e.target.value)} /></div>
          <div><label className="text-xs font-semibold text-slate-500 uppercase">Min Experience</label><input type="number" className={inputCls} value={f.min_experience} onChange={(e) => set("min_experience", e.target.value)} /></div>
          <div><label className="text-xs font-semibold text-slate-500 uppercase">Max Experience</label><input type="number" className={inputCls} value={f.max_experience} onChange={(e) => set("max_experience", e.target.value)} /></div>
          <div><label className="text-xs font-semibold text-slate-500 uppercase">Min Cable Exp</label><input type="number" className={inputCls} value={f.min_cable_experience} onChange={(e) => set("min_cable_experience", e.target.value)} /></div>
          <div><label className="text-xs font-semibold text-slate-500 uppercase">Candidates Needed</label><input type="number" className={inputCls} value={f.required_count} onChange={(e) => set("required_count", parseInt(e.target.value) || 1)} /></div>
          <MultiSelect label="Products" options={tax.products || []} value={f.products} onChange={(v) => set("products", v)} testid="req-products" />
          <MultiSelect label="Processes" options={tax.all_processes || []} value={f.processes} onChange={(v) => set("processes", v)} testid="req-processes" />
          <MultiSelect label="Skills" options={tax.skills || []} value={f.skills} onChange={(v) => set("skills", v)} testid="req-skills" />
          <MultiSelect label="Education" options={tax.education_degrees || []} value={f.education} onChange={(v) => set("education", v)} testid="req-education" />
          <MultiSelect label="Departments" options={tax.departments || []} value={f.departments} onChange={(v) => set("departments", v)} testid="req-departments" />
          <MultiSelect label="Designations" options={tax.designations || []} value={f.designations} onChange={(v) => set("designations", v)} testid="req-designations" />
        </div>
        <div className="flex justify-end gap-3 mt-5"><button onClick={onClose} className="px-5 py-2.5 border border-slate-300 rounded-lg text-slate-700">Cancel</button><button onClick={submit} data-testid="submit-requirement-btn" className="copper-gradient text-white font-semibold px-6 py-2.5 rounded-lg">Create & Match</button></div>
      </div>
    </div>
  );
}
